import React, { useEffect, useState } from 'react';
import { WarningCircle, X, Check, Info } from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import {
  fetchBranches, fetchGroups, fetchTickets, fetchBpoGuarantors, submitDeleteBpoGuarantors, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult, inlineNoticeColor } from '../utils/noData';

function BpoGuarantorDeleteForm({ connectionId }) {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);
  const [branches, setBranches] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [branchCode, setBranchCode] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [ticketNo, setTicketNo] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingTicketNo, setPendingTicketNo] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  const selectedRows = rows.filter((r) => r.selected);
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);
  const isValid = selectedRows.length > 0 && reason.trim().length >= 10 && reason.length <= 200;

  const showToast = (severity, summary, detail, sticky = false) => {
    setToast({ severity, summary, detail, sticky });
    if (!sticky) setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (!connectionId) return;
    (async () => {
      try {
        setBranches(await fetchBranches(connectionId));
      } catch (e) {
        setLoadError('Could not load branches from the client API');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId]);

  const handleBranch = async (value) => {
    setBranchCode(value);
    setGroupCode('');
    setTicketNo('');
    setGroups([]);
    setTickets([]);
    setRows([]);
    if (!value) return;
    try {
      const groups = await fetchGroups(connectionId, value);
      setGroups(groups);
      if (isEmptyResult(groups)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (e) {
      setLoadError('Could not load groups for that branch');
    }
  };

  const handleGroup = async (value) => {
    setGroupCode(value);
    setTicketNo('');
    setTickets([]);
    setRows([]);
    if (!value) return;
    try {
      const list = await fetchTickets(connectionId, branchCode, value);
      setTickets(list);
      if (isEmptyResult(list)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (e) {
      setLoadError('Could not load tickets for that group');
    }
  };

  const handleTicket = async (value) => {
    setTicketNo(value);
    setRows([]);
    if (!value) return;
    setLoading(true);
    setLoadError('');
    try {
      const guarantors = await fetchBpoGuarantors(connectionId, branchCode, groupCode, Number(value));
      setRows((guarantors || []).map((g) => ({ ...g, selected: false })));
      if (isEmptyResult(guarantors)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (e) {
      setLoadError('Could not load guarantor records for that ticket');
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (index) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, selected: !r.selected } : r)));
  };

  const toggleSelectAll = () => {
    const allSelected = rows.length > 0 && rows.every((r) => r.selected);
    setRows((prev) => prev.map((r) => ({ ...r, selected: !allSelected })));
  };

  /** Step 1: validate, stage the pending delete, open the confirm modal. */
  const confirmDelete = () => {
    setTouched(true);
    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please select at least one guarantor and enter a valid reason.');
      return;
    }

    setPendingCount(selectedRows.length);
    setPendingTicketNo(ticketNo);
    setPendingReason(reason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  /** Step 2: fires only after the user clicks "Yes, Delete" in the modal. */
  const doDelete = async () => {
    setSaving(true);
    try {
      const group = groups.find((g) => g.group_Code === groupCode);
      await submitDeleteBpoGuarantors(connectionId, {
        branchCode,
        chitgroupId: Number(group.chitgroup_id),
        ticketNo: Number(ticketNo),
        guarantors: selectedRows.map((r) => ({ contactId: r.contactId, guarantorId: r.guarantorId })),
        reason,
      });
      try {
        await saveData([{
          schemaName: branchCode || null,
          loginName,
          loginTime: new Date().toISOString(),
          changeType: 'BPO_GUARANTOR_DELETE',
          oldData: {
            branchCode,
            groupCode,
            ticketNo: Number(ticketNo),
            guarantors: selectedRows.map((r) => ({
              contactId: r.contactId, guarantorId: r.guarantorId, guarantorName: r.guarantorName,
            })),
          },
          newData: { status: 'DELETED', deletedCount: selectedRows.length },
          reason: reason.trim(),
        }]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', `${pendingCount} guarantor record(s) removed from ticket ${pendingTicketNo}.`, true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Guarantor record(s) deleted, but audit log failed to save.', true);
        handleClear();
      }
    } catch (e) {
      setSaving(false);
      setShowConfirmModal(false);
      showToast('error', 'Delete Failed', e?.response?.data?.detail || 'Could not delete these guarantor records. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setBranchCode('');
    setGroupCode('');
    setTicketNo('');
    setGroups([]);
    setTickets([]);
    setRows([]);
    setLoadError('');
    setReason('');
    setTouched(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="bpo-guarantor-delete-toast" />

      <div key={formKey} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="bpo-guarantor-delete-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
        <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>BPO Guarantor Id Delete</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group and ticket to load its guarantor records</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>CAO *</label>
            <SearchableSelect
              options={branches}
              value={branchCode}
              onChange={handleBranch}
              getLabel={(b) => b.branch_name}
              getValue={(b) => b.branch_code}
              placeholder="Search branch..."
              testId="bpo-guarantor-delete-branch"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Group Code *</label>
            <SearchableSelect
              options={groups}
              value={groupCode}
              onChange={handleGroup}
              getLabel={(g) => g.group_Code}
              getValue={(g) => g.group_Code}
              placeholder={'Search group code...'}
              disabled={!branchCode}
              testId="bpo-guarantor-delete-group"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Ticket No *</label>
            <SearchableSelect
              options={tickets}
              value={ticketNo}
              onChange={handleTicket}
              getLabel={(t) => t.ticketno}
              getValue={(t) => String(t.ticketno)}
              placeholder={'Search ticket...'}
              disabled={!groupCode}
              testId="bpo-guarantor-delete-ticket"
            />
          </div>
        </div>
        {loadError && <div style={{ fontSize: '11px', color: inlineNoticeColor(loadError), marginTop: '8px' }}>{loadError}</div>}
        {loading && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '8px' }}>Loading guarantor records...</div>}

        {ticketNo && !loading && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Guarantor Records</div>
                  <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Select the guarantor record(s) to delete</div>
                </div>
              </div>
              {selectedRows.length > 0 && (
                <span style={{ fontSize: '11px', fontWeight: '600', color: 'white', background: '#002FA7', padding: '4px 8px', borderRadius: '4px' }}>
                  {selectedRows.length} selected
                </span>
              )}
            </div>

            {rows.length === 0 ? (
              <div style={{ padding: '12px', fontSize: '13px', color: '#9CA3AF', border: '1px dashed #E5E7EB', borderRadius: '6px' }} data-testid="bpo-guarantor-delete-empty">
                No guarantor records found for the selected ticket.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }} data-testid="bpo-guarantor-delete-table">
                <thead>
                  <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left', color: '#6B7280', fontSize: '11px' }}>
                    <th style={{ padding: '8px', width: '32px' }}>
                      <input type="checkbox" checked={rows.length > 0 && rows.every((r) => r.selected)} onChange={toggleSelectAll} data-testid="bpo-guarantor-delete-select-all" />
                    </th>
                    <th style={{ padding: '8px' }}>Guarantor ID</th>
                    <th style={{ padding: '8px' }}>Contact ID</th>
                    <th style={{ padding: '8px' }}>Guarantor Name</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.guarantorId} style={{ borderBottom: '1px solid #F3F4F6', background: r.selected ? '#EFF6FF' : undefined }}>
                      <td style={{ padding: '8px' }}>
                        <input type="checkbox" checked={r.selected} onChange={() => toggleRow(i)} data-testid={`bpo-guarantor-delete-row-${i}`} />
                      </td>
                      <td style={{ padding: '8px', fontFamily: 'monospace' }}>{r.guarantorId}</td>
                      <td style={{ padding: '8px', fontFamily: 'monospace' }}>{r.contactId}</td>
                      <td style={{ padding: '8px' }}>{r.guarantorName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {rows.length > 0 && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Deletion</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
              </div>
            </div>
            <textarea
              className="input-field"
              style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
              rows={3}
              maxLength={200}
              placeholder="Enter reason (min 10 characters)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="bpo-guarantor-delete-reason"
            />
            <div style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : '#9CA3AF', marginTop: '2px', textAlign: 'right' }}>
              {reason.length} / 200
            </div>
            {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626' }}>Minimum 10 characters required</div>}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="bpo-guarantor-delete-clear-button">Clear</button>
          <button
            type="button"
            onClick={confirmDelete}
            className="btn-primary"
            style={{ background: selectedRows.length > 0 ? '#DC2626' : undefined }}
            disabled={selectedRows.length === 0 || saving}
            data-testid="bpo-guarantor-delete-delete-button"
          >
            {saving ? 'Deleting...' : `Delete${selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}`}
          </button>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="bpo-guarantor-delete-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <WarningCircle size={18} color="#DC2626" weight="fill" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Guarantor Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to delete <strong>{pendingCount}</strong> guarantor record(s) from ticket <strong>{pendingTicketNo}</strong>. This action cannot be undone.
              </p>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="bpo-guarantor-delete-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={saving}
                data-testid="bpo-guarantor-delete-confirm-delete"
                style={{
                  display: 'inline-flex', alignItems: 'center', padding: '8px 16px', fontSize: '13px', fontWeight: '600',
                  color: 'white', background: saving ? '#F3A6A6' : '#DC2626', border: 'none', borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BpoGuarantorDeleteForm;